import { useMutation, useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { usersApi } from '@/features/users'

/**
 * Dev-only user impersonation page (ADR-0009, Spec 0 Group 11).
 *
 * Admin-gated (via <RequireRole> in the router) and stage-gated (the route is
 * only registered on non-prod stages). This component assumes both gates are
 * upstream — it does no role check itself.
 *
 * Cookie-based: the backend swaps the `access_token` cookie server-side and
 * stashes the admin's JWT in `original_access_token`. The frontend never
 * reads or writes either token.
 */

export function SwitchUserPage() {
	const [errorMessage, setErrorMessage] = useState<string | null>(null)
	const { user } = useAuth()
	const alreadyImpersonating = Boolean(user?.impersonatedBy)

	const usersQuery = useQuery({
		queryKey: ['switch-user', 'users'],
		queryFn: () => usersApi.listUsers(),
		staleTime: 30_000,
	})

	const impersonateMutation = useMutation({
		mutationFn: (userId: string) => usersApi.devImpersonate(userId),
		onSuccess: () => {
			// Full reload so every hook / cache / query re-initialises under the
			// impersonated identity. No ambiguity about stale state.
			window.location.reload()
		},
		onError: (err: unknown) => {
			if (isAxiosError(err)) {
				const message = (err.response?.data as { message?: string } | undefined)?.message
				setErrorMessage(message ?? `Request failed (${err.response?.status ?? 'unknown'})`)
			} else {
				setErrorMessage('Unexpected error starting impersonation')
			}
		},
	})

	const handleImpersonate = (userId: string) => {
		setErrorMessage(null)
		impersonateMutation.mutate(userId)
	}

	return (
		<div className="max-w-4xl mx-auto py-6 space-y-4">
			<div>
				<h1 className="text-2xl font-semibold text-foreground">Impersonate user (dev)</h1>
				<p className="text-sm text-muted-foreground mt-1">
					Assume another user's identity for testing. Admin-only, non-prod only. Every impersonation is audit-logged
					server-side.
				</p>
			</div>

			{errorMessage && (
				<div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{errorMessage}</div>
			)}

			{alreadyImpersonating && (
				<div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
					You are already impersonating another user. Use the banner's "Return to self" button before switching again.
				</div>
			)}

			{usersQuery.isLoading && <div className="text-sm text-muted-foreground">Loading users…</div>}
			{usersQuery.isError && <div className="text-sm text-red-600">Failed to load users</div>}

			{usersQuery.data && (
				<table className="w-full border-collapse text-sm">
					<thead>
						<tr className="text-left text-muted-foreground border-b border-border">
							<th className="py-2 pr-4 font-medium">Email</th>
							<th className="py-2 pr-4 font-medium">Display name</th>
							<th className="py-2 pr-4 font-medium">Role</th>
							<th className="py-2 pr-4 font-medium">Status</th>
							<th className="py-2 font-medium text-right">Action</th>
						</tr>
					</thead>
					<tbody>
						{usersQuery.data.map((u) => (
							<tr key={u.userId} className="border-b border-border">
								<td className="py-2 pr-4">{u.email}</td>
								<td className="py-2 pr-4">{u.displayName}</td>
								<td className="py-2 pr-4">{u.role}</td>
								<td className="py-2 pr-4">{u.isActive ? 'Active' : 'Inactive'}</td>
								<td className="py-2 text-right">
									<button
										type="button"
										onClick={() => handleImpersonate(u.userId)}
										disabled={alreadyImpersonating || impersonateMutation.isPending}
										className="rounded-md bg-foreground px-3 py-1 text-xs font-medium text-white hover:bg-foreground/90 disabled:cursor-not-allowed disabled:opacity-50"
									>
										Impersonate
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</div>
	)
}
